import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Calendar, Clock, Search } from "lucide-react";
import { useState, useMemo, JSX } from "react";
import { blogPosts, blogCategories } from "@/data";
import { formatDate, cn } from "@/lib/utils";

export const Route = createFileRoute("/_public/blog/")({
  component: BlogPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BlogPage(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("All Posts");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory =
        activeCategory === "All Posts" || post.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some((t) =>
          t.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <>
      <Helmet>
        <title>Tax Insights & Blog | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Expert insights on Kenyan tax law, KRA compliance, eTIMS, PAYE, and international tax. Stay informed with Rosewood's tax blog."
        />
      </Helmet>

      <section className="bg-cream pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className=" text-5xl md:text-6xl font-semibold mb-4">
              Blog & Insights
            </h1>
            <p className="text-lg text-neutral-800">
              Expert analysis, compliance guides, and the latest Kenya tax news
              from our team.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-800"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl text-sm border focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium transition-all",
                    activeCategory === cat
                      ? "bg-burgundy text-white"
                      : "bg-white text-neutral-800 hover:bg-burgundy/10 hover:text-burgundy",
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-800">
                No articles found matching your search.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <Link
                    to={`/blog/${post.slug}` as never}
                    className="group block h-full"
                  >
                    <div className="h-full rounded-xl overflow-hidden hover:shadow-xs hover:-translate-y-0.5 transition-all flex flex-col">
                      <div className="aspect-[16/9] bg-burgundy/8 rounded-xl mb-5 overflow-hidden relative">
                        {post.coverImage ? (
                          <img
                            src={post.coverImage}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-burgundy/15 flex items-center justify-center">
                              <span className="text-2xl font-bold text-burgundy/40">
                                {post.category.charAt(0)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="py-5 flex-1 flex flex-col">
                        <div className="flex items-center gap-4 text-neutral-800 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar size={11} />
                            {formatDate(post.publishedAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={11} />
                            {post.readTime} min
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 leading-snug group-hover:text-burgundy transition-colors line-clamp-2 flex-1">
                          {post.title}
                        </h3>
                        <p className="text-sm text-neutral-800 line-clamp-2 mb-4">
                          {post.excerpt}
                        </p>
                        {/* <div className="flex items-center gap-2 mt-auto">
                          <div className="w-7 h-7 bg-burgundy/10 rounded-full flex items-center justify-center">
                            <span className="text-burgundy text-[11px] font-bold">
                              {post.author.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-[#1A1A1A]">
                            {post.author.name}
                          </span>
                        </div> */}
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
